import React from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Modal, Form, Select, DatePicker, Input, notification, Typography, Space, Tag } from 'antd';
import { PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useCompanyId } from '../../lib/books';

import { date as fmtDate, label } from '../../lib/format';
const STATUS_COLORS = { ACTIVE: 'green', PAUSED: 'orange', ENDED: 'default' };

/** Firm-wide service engagement list - the company's own subscriptions plus its clients'. */
export default function EngagementsPage() {
  const auth = useAuth();
  const isSystemAdmin = auth.user.role === 'SYSTEM_ADMIN';
  const { companyId, setCompanyId } = useCompanyId();
  const [companies, setCompanies] = React.useState([]);
  const [clients, setClients] = React.useState([]);
  const [services, setServices] = React.useState([]);
  const [departments, setDepartments] = React.useState([]);
  const [engagements, setEngagements] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [activating, setActivating] = React.useState(null);
  const [forType, setForType] = React.useState('client');
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (isSystemAdmin) {
      request
        .get('/companies')
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            setCompanies(r.data);
            if (!companyId && r.data.length) setCompanyId(r.data[0].id);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    if (!companyId) return;
    request
      .get('/services', { company_id: companyId })
      .then((r) => r.json())
      .then((r) => r.status && setServices(r.data));
  }, [companyId]);

  const fetchAll = () => {
    if (!companyId) return;
    setLoading(true);
    Promise.all([
      request.get('/client-service-engagements', { company_id: companyId }).then((r) => r.json()),
      request.get('/clients', { company_id: companyId }).then((r) => r.json()),
      request.get('/departments', { company_id: companyId }).then((r) => r.json()),
    ])
      .then(([engagementsRes, clientsRes, departmentsRes]) => {
        if (engagementsRes.status) setEngagements(engagementsRes.data);
        if (clientsRes.status) setClients(clientsRes.data);
        if (departmentsRes.status) setDepartments(departmentsRes.data);
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchAll, [companyId]);

  const handleCreate = () => {
    form.validateFields().then((values) => {
      const { for_type, for_id, ...rest } = values;
      const payload = {
        ...rest,
        company_id: for_type === 'company' ? companyId : null,
        client_id: for_type === 'client' ? for_id : null,
        costing_company_id: companyId,
        start_date: values.start_date.format('YYYY-MM-DD'),
      };
      request
        .post('/client-service-engagements', payload)
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: 'Engagement created' });
            setModalOpen(false);
            fetchAll();
          } else {
            notification.error({ message: r.message });
          }
        });
    });
  };

  const handleActivate = (engagement) => {
    setActivating(engagement.id);
    request
      .post(`/client-service-engagements/${engagement.id}/activate`, {})
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: `Activated - ${r.data.tasksCreated} task(s) created` });
          fetchAll();
        } else {
          notification.error({ message: r.message });
        }
      })
      .finally(() => setActivating(null));
  };

  const columns = [
    {
      title: 'For',
      key: 'for',
      render: (_, e) => (e.Client ? <Link to={`/clients/${e.Client.id}`}>{e.Client.client_name}</Link> : 'This Company'),
    },
    { title: 'Service', key: 'service', render: (_, e) => e.Service?.service_name || '-' },
    { title: 'Frequency', dataIndex: 'frequency', render: (v) => label(v) },
    { title: 'Start Date', dataIndex: 'start_date', render: (v) => fmtDate(v) },
    { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={STATUS_COLORS[v]}>{label(v)}</Tag> },
    {
      title: '',
      key: 'actions',
      render: (_, e) =>
        e.project_id ? (
          <Tag color="blue">Activated (Project #{e.project_id})</Tag>
        ) : (
          <Button size="small" icon={<ThunderboltOutlined />} loading={activating === e.id} onClick={() => handleActivate(e)}>
            Activate
          </Button>
        ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Service Engagements
        </Typography.Title>
        <Space>
          {isSystemAdmin && (
            <Select
              style={{ width: 200 }}
              value={companyId}
              placeholder="Company"
              onChange={setCompanyId}
              options={companies.map((c) => ({ value: c.id, label: c.name }))}
            />
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              setForType('client');
              form.setFieldsValue({ for_type: 'client', frequency: 'MONTHLY' });
              setModalOpen(true);
            }}
          >
            New Engagement
          </Button>
        </Space>
      </Space>

      <Table rowKey="id" columns={columns} dataSource={engagements} loading={loading} />

      <Modal title="New Service Engagement" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleCreate}>
        <Form form={form} layout="vertical">
          <Form.Item name="for_type" label="Engagement For" rules={[{ required: true }]}>
            <Select
              onChange={(v) => {
                setForType(v);
                form.setFieldsValue({ for_id: null });
              }}
              options={[
                { value: 'client', label: 'A Client' },
                { value: 'company', label: 'This Company (internal)' },
              ]}
            />
          </Form.Item>
          {forType === 'client' && (
            <Form.Item name="for_id" label="Client" rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="label" options={clients.map((c) => ({ value: c.id, label: c.client_name }))} />
            </Form.Item>
          )}
          <Form.Item name="service_id" label="Service" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" options={services.map((s) => ({ value: s.id, label: s.service_name }))} />
          </Form.Item>
          <Form.Item name="frequency" label="Frequency" rules={[{ required: true }]}>
            <Select options={['ONE_OFF', 'MONTHLY', 'QUARTERLY', 'ANNUALLY'].map((v) => ({ value: v, label: v }))} />
          </Form.Item>
          <Form.Item name="start_date" label="Start Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="responsible_department_id" label="Responsible Department">
            <Select allowClear options={departments.map((d) => ({ value: d.id, label: d.name }))} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
